import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createABTest, getTasks } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Flask } from '@phosphor-icons/react';

export default function CreateABTestPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedTaskId = searchParams.get('task') || '';
  const [tasks, setTasks] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    linked_task_id: preselectedTaskId, test_name: '', start_date: '', end_date: '',
    audience_split: '50:50', target_audience: 'All', testing_domain: 'wellversed.in',
    custom_domain: '', primary_metric: 'Conversion Rate',
    variant_a_result: '', variant_b_result: '',
    winner: '', confidence_level: '', decision: '', status: 'Not Started',
    key_insights: '', observations: '', analytics_links: ''
  });

  useEffect(() => {
    getTasks({}).then(r => setTasks(r.data)).catch(err => console.error('Load tasks failed:', err.message));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (preselectedTaskId) {
      setForm(prev => ({ ...prev, linked_task_id: preselectedTaskId }));
    }
  }, [preselectedTaskId]);

  const update = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.linked_task_id || !form.test_name.trim()) return;
    setSubmitting(true);
    try {
      const payload = { ...form };
      // Clean up empty strings to null
      ['start_date', 'end_date', 'custom_domain', 'winner', 'confidence_level', 'decision', 'key_insights', 'observations', 'analytics_links'].forEach(f => {
        if (!payload[f]) payload[f] = null;
      });
      ['variant_a_result', 'variant_b_result'].forEach(f => {
        if (payload[f] === '' || payload[f] === null) payload[f] = null;
        else payload[f] = parseFloat(payload[f]);
      });
      const res = await createABTest(payload);
      navigate(`/ab-tests/${res.data.id}`);
    } catch (e) {
      console.error(e);
      alert('Failed to create A/B test');
    }
    setSubmitting(false);
  };

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="create-ab-test-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-[#555555] hover:text-[#0F0F0F] mb-5 transition-colors" data-testid="ab-back-btn">
        <ArrowLeft size={16} /> Back
      </button>

      <h1 className="font-['Manrope'] text-2xl font-bold tracking-tight text-[#0F0F0F] mb-6 flex items-center gap-2">
        <Flask size={24} weight="fill" className="text-[#32B4AC]" />
        Create A/B Test
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="border border-[#E8E8E8] rounded-md p-5 space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555]">Basic Info</h3>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Linked Task *</Label>
            <select data-testid="ab-linked-task-select" value={form.linked_task_id} onChange={update('linked_task_id')} className="w-full h-10 text-sm border border-[#E8E8E8] rounded-md px-3 bg-white" required>
              <option value="">Select a task</option>
              {tasks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Test Name *</Label>
            <Input data-testid="ab-test-name-input" value={form.test_name} onChange={update('test_name')} placeholder="e.g., Homepage Hero Banner v2" className="border-[#E8E8E8] rounded-md h-10" required />
          </div>
        </div>

        {/* Experiment Setup */}
        <div className="border border-[#E8E8E8] rounded-md p-5 space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555]">Experiment Setup</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Start Date</Label>
              <Input data-testid="ab-start-date-input" type="date" value={form.start_date} onChange={update('start_date')} className="border-[#E8E8E8] rounded-md h-10" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">End Date</Label>
              <Input data-testid="ab-end-date-input" type="date" value={form.end_date} onChange={update('end_date')} className="border-[#E8E8E8] rounded-md h-10" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Audience Split</Label>
              <Input data-testid="ab-audience-split-input" value={form.audience_split} onChange={update('audience_split')} placeholder="50:50" className="border-[#E8E8E8] rounded-md h-10" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Target Audience</Label>
              <select data-testid="ab-target-audience-select" value={form.target_audience} onChange={update('target_audience')} className="w-full h-10 text-sm border border-[#E8E8E8] rounded-md px-3 bg-white">
                <option value="All">All</option>
                <option value="New users">New users</option>
                <option value="Returning users">Returning users</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Testing Domain</Label>
              <select data-testid="ab-testing-domain-select" value={form.testing_domain} onChange={update('testing_domain')} className="w-full h-10 text-sm border border-[#E8E8E8] rounded-md px-3 bg-white">
                <option value="wellversed.in">wellversed.in</option>
                <option value="store.wellversed.in">store.wellversed.in</option>
                <option value="Other">Other</option>
              </select>
            </div>
            {form.testing_domain === 'Other' && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Custom Domain</Label>
                <Input data-testid="ab-custom-domain-input" value={form.custom_domain} onChange={update('custom_domain')} placeholder="youwefit.com" className="border-[#E8E8E8] rounded-md h-10" />
              </div>
            )}
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Status</Label>
              <select data-testid="ab-status-select" value={form.status} onChange={update('status')} className="w-full h-10 text-sm border border-[#E8E8E8] rounded-md px-3 bg-white">
                <option value="Not Started">Not Started</option>
                <option value="Running">Running</option>
                <option value="Completed">Completed</option>
                <option value="Implemented">Implemented</option>
              </select>
            </div>
          </div>
        </div>

        {/* Metrics */}
        <div className="border border-[#E8E8E8] rounded-md p-5 space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555]">Metrics (optional - can be added later)</h3>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Primary Metric</Label>
            <Input data-testid="ab-primary-metric-input" value={form.primary_metric} onChange={update('primary_metric')} placeholder="Conversion Rate" className="border-[#E8E8E8] rounded-md h-10" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Variant A Result</Label>
              <Input data-testid="ab-variant-a-result-input" type="number" step="0.01" value={form.variant_a_result} onChange={update('variant_a_result')} placeholder="e.g., 3.2" className="border-[#E8E8E8] rounded-md h-10" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Variant B Result</Label>
              <Input data-testid="ab-variant-b-result-input" type="number" step="0.01" value={form.variant_b_result} onChange={update('variant_b_result')} placeholder="e.g., 4.1" className="border-[#E8E8E8] rounded-md h-10" />
            </div>
          </div>
        </div>

        {/* Outcome */}
        <div className="border border-[#E8E8E8] rounded-md p-5 space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555]">Outcome (optional - can be added later)</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Winner</Label>
              <select data-testid="ab-winner-select" value={form.winner} onChange={update('winner')} className="w-full h-10 text-sm border border-[#E8E8E8] rounded-md px-3 bg-white">
                <option value="">Not decided</option>
                <option value="A">Variant A</option>
                <option value="B">Variant B</option>
                <option value="Inconclusive">Inconclusive</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Confidence</Label>
              <select data-testid="ab-confidence-select" value={form.confidence_level} onChange={update('confidence_level')} className="w-full h-10 text-sm border border-[#E8E8E8] rounded-md px-3 bg-white">
                <option value="">Not set</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Decision</Label>
              <select data-testid="ab-decision-select" value={form.decision} onChange={update('decision')} className="w-full h-10 text-sm border border-[#E8E8E8] rounded-md px-3 bg-white">
                <option value="">No decision</option>
                <option value="Implement Variant A">Implement Variant A</option>
                <option value="Implement Variant B">Implement Variant B</option>
                <option value="Iterate Further">Iterate Further</option>
                <option value="Rollback">Rollback</option>
              </select>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="border border-[#E8E8E8] rounded-md p-5 space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555]">Notes & Learnings (optional)</h3>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Key Insights</Label>
            <Textarea data-testid="ab-insights-input" value={form.key_insights} onChange={update('key_insights')} placeholder="What did we learn from this experiment?" className="min-h-[80px] border-[#E8E8E8] rounded-md resize-none" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Analytics Links</Label>
            <Input data-testid="ab-analytics-links-input" value={form.analytics_links} onChange={update('analytics_links')} placeholder="https://analytics.google.com/..." className="border-[#E8E8E8] rounded-md h-10" />
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3 pt-4 border-t border-[#E8E8E8]">
          <button type="button" onClick={() => navigate(-1)} className="h-10 px-5 text-sm border border-[#1A1A1A] text-[#0F0F0F] rounded-md hover:bg-[#F4F4F4] transition-colors" data-testid="ab-cancel-btn">
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !form.linked_task_id || !form.test_name.trim()}
            className="h-10 px-5 bg-[#32B4AC] text-white text-sm font-semibold rounded-md hover:bg-[#2A9B94] disabled:opacity-50 transition-colors flex items-center gap-2"
            data-testid="ab-submit-btn"
          >
            {submitting ? 'Creating...' : 'Create A/B Test'}
          </button>
        </div>
      </form>
    </div>
  );
}
